import { Button, cn } from '@suscri/ui';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface EmptyStateProps {
  icon: LucideIcon;
  title: ReactNode;
  description: ReactNode;
  primaryAction?: { label: ReactNode; onClick: () => void };
  secondaryAction?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl border border-dashed border-primary/30 bg-primary/[0.055] px-5 py-10 text-center sm:px-8 sm:py-12',
        className
      )}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--primary)/0.15),transparent_55%)]" />
      <div className="relative mx-auto flex max-w-xl flex-col items-center">
        <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/15 text-primary shadow-glow">
          <Icon className="h-7 w-7" aria-hidden="true" />
        </span>
        <h2 className="text-lg font-semibold text-foreground sm:text-xl">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
        {(primaryAction || secondaryAction) && (
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
            {secondaryAction}
            {primaryAction && (
              <Button onClick={primaryAction.onClick}>{primaryAction.label}</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
